import { apiBaseUrl, useMockApi, useDevAuth, devUserId } from "./config";
import { handle as mockFriends } from "../mocks/friends";
import { handle as mockGroups } from "../mocks/groups";
import { handle as mockUpcomingPayments } from "../mocks/upcoming-payments";
import { handle as mockRequests } from "../mocks/requests";
import { handle as mockTransactions } from "../mocks/transactions";
import { handle as mockNotifications } from "../mocks/notifications";
import { handle as mockBillSplits } from "../mocks/bill-splits";
import { handle as mockAuth } from "../mocks/auth";
import { handle as mockUsers } from "../mocks/users";
import { handle as mockContacts } from "../mocks/contacts";
import { handle as mockCatalog } from "../mocks/catalog";
import { handle as mockOrders } from "../mocks/orders";
import { handle as mockPaymentMethods } from "../mocks/payment-methods";
import { clearAuth } from "./auth";
import { clearBiometricCredential } from "./biometric-storage";
import { handle as mockPayLinks } from "../mocks/pay-links";

type MockHandler = (path: string, init?: RequestInit) => Promise<any>;

const mockRoutes: Array<{ test: RegExp; handler: MockHandler }> = [
  { test: /^\/contacts/, handler: mockContacts },
  { test: /^\/friends/, handler: mockFriends },
  { test: /^\/groups/, handler: mockGroups },
  { test: /^\/upcoming-payments/, handler: mockUpcomingPayments },
  { test: /^\/requests/, handler: mockRequests },
  { test: /^\/auth/, handler: mockAuth },
  { test: /^\/users/, handler: mockUsers },
  { test: /^\/api\/users/, handler: mockUsers },
  { test: /^\/api\/transactions(\/|$)/, handler: mockTransactions },
  { test: /^\/api\/notifications(\/|$)/, handler: mockNotifications },
  { test: /^\/api\/pay-links(\/|$)/, handler: mockPayLinks },
  { test: /^\/pay-links(\/|$)/, handler: mockPayLinks },
  { test: /^\/bill-splits(\/|$)/, handler: mockBillSplits },
  { test: /^\/api\/catalog(\/|$)/, handler: mockCatalog },
  { test: /^\/catalog(\/|$)/, handler: mockCatalog },
  { test: /^\/api\/orders(\/|$)/, handler: mockOrders },
  { test: /^\/orders(\/|$)/, handler: mockOrders },
  { test: /^\/api\/payment-methods(\/|$)/, handler: mockPaymentMethods },
  { test: /^\/payment-methods(\/|$)/, handler: mockPaymentMethods },
];

let cachedAuthString: string | null | undefined;
let cachedToken: string | null = null;
let cachedUserString: string | null | undefined;
let cachedUserId: string | null = null;

function resetAuthCache() {
  cachedAuthString = undefined;
  cachedToken = null;
  cachedUserString = undefined;
  cachedUserId = null;
}

function readAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  const authString = window.localStorage.getItem("biltip_auth");
  if (authString === cachedAuthString) {
    return cachedToken;
  }

  cachedAuthString = authString;
  if (!authString) {
    cachedToken = null;
    return null;
  }

  try {
    const parsed = JSON.parse(authString);
    cachedToken = typeof parsed?.token === "string" ? parsed.token : null;
  } catch {
    cachedToken = null;
  }

  return cachedToken;
}

function readStoredUserId(): string | null {
  if (typeof window === "undefined") return null;
  const userString = window.localStorage.getItem("biltip_user");
  if (userString === cachedUserString) {
    return cachedUserId;
  }

  cachedUserString = userString;
  if (!userString) {
    cachedUserId = null;
    return null;
  }

  try {
    const parsed = JSON.parse(userString);
    cachedUserId = parsed?.id ? String(parsed.id) : null;
  } catch {
    cachedUserId = null;
  }

  return cachedUserId;
}

function joinUrl(base: string, resource: string): string {
  // Absolute resource wins
  if (/^https?:\/\//i.test(resource)) return resource;

  // Normalize base (remove trailing slashes)
  const baseNorm = (base || "").replace(/\/+$/, "");

  // If base is absolute, preserve its pathname when joining
  const isAbsoluteBase = /^https?:\/\//i.test(baseNorm);
  if (isAbsoluteBase) {
    const u = new URL(baseNorm);
    const basePath = u.pathname.replace(/\/+$/, "");
    const baseHasApi = /(^|\/)api(\/|$)/.test(basePath);
    // Ensure resource has a leading slash
    let resPath = resource.startsWith("/") ? resource : `/${resource}`;
    // If base path doesn't include '/api' and resource doesn't start with it, prefix '/api'
    if (!baseHasApi && !resPath.startsWith("/api/")) {
      resPath = `/api${resPath}`;
    }
    // If resource already includes the basePath prefix, avoid duplicating it
    const finalPath =
      resPath.startsWith(basePath + "/") || resPath === basePath
        ? resPath
        : `${basePath}${resPath}`;
    return `${u.origin}${finalPath}`;
  }

  // Relative base (e.g., '/api'): ensure exactly one slash between base and resource
  // If resource already starts with '/api', avoid duplicating when base is also '/api'.
  if (resource.startsWith("/api/")) {
    return resource.replace(/\/{2,}/g, "/");
  }
  const left = baseNorm || "";
  const right = resource.replace(/^\/+/, "");
  const joined = `${left}/${right}`;
  // Collapse multiple slashes in the path (but leave protocol intact — not applicable here)
  return joined.replace(/\/{2,}/g, "/");
}

export async function apiClient(
  input: RequestInfo | URL,
  init: RequestInit = {},
) {
  const token = readAuthToken();

  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };
  // token is expected to be a JWT (three base64url segments)
  // Only attach the Authorization header for syntactically valid JWTs.
  // Dev auth should be handled via `useDevAuth` (x-user-id) instead.
  const tokenIsValid =
    typeof token === "string" &&
    /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+$/.test(token);

  // Reduce console noise during development to improve startup performance

  if (tokenIsValid) {
    headers["Authorization"] = `Bearer ${token}`;
    // Authorization header attached
  } else {
    if (token) {
      console.warn("Invalid auth token, Authorization header omitted");
      clearAuth();
      clearBiometricCredential();
      resetAuthCache();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("session-expired"));
      }
    }
  }

  // Dev-mode auth bypass using backend's x-user-id hook
  if (!headers["Authorization"] && useDevAuth) {
    // Prefer explicit env user id, else fall back to stored user id
    const xUserId = devUserId || readStoredUserId();
    if (xUserId) {
      headers["x-user-id"] = xUserId;
    }
    // x-user-id header appended for dev auth
  }

  const resource =
    typeof input === "string"
      ? input
      : input instanceof URL
        ? input.pathname + input.search
        : String(input);
  const url = joinUrl(apiBaseUrl, resource);

  if (useMockApi) {
    const toPath = (value: string | URL, fallbackBase?: string): string => {
      try {
        if (value instanceof URL) return value.pathname;
        if (/^https?:\/\//i.test(value)) {
          return new URL(value).pathname;
        }
        return new URL(value, fallbackBase || "http://mock.local").pathname;
      } catch {
        const str = String(value ?? "");
        if (!str) return "/";
        return str.startsWith("/")
          ? str.split("?")[0]
          : `/${str}`.replace(/\/{2,}/g, "/").split("?")[0];
      }
    };

    const candidatePaths = new Set<string>();
    candidatePaths.add(
      toPath(
        resource,
        typeof window !== "undefined"
          ? window.location.origin
          : "http://mock.local",
      ),
    );
    candidatePaths.add(
      toPath(
        url,
        typeof window !== "undefined"
          ? window.location.origin
          : "http://mock.local",
      ),
    );

    const normalizedBasePath = (() => {
      try {
        if (!apiBaseUrl) return "";
        const parsed = new URL(
          apiBaseUrl,
          apiBaseUrl.startsWith("http") ? undefined : "http://mock.local",
        );
        return parsed.pathname.replace(/\/+$/, "") || "/";
      } catch {
        return String(apiBaseUrl || "").replace(/\/+$/, "") || "/";
      }
    })();

    for (const path of Array.from(candidatePaths)) {
      if (!path) continue;
      if (
        normalizedBasePath &&
        normalizedBasePath !== "/" &&
        path.startsWith(normalizedBasePath) &&
        path !== normalizedBasePath
      ) {
        candidatePaths.add(path.slice(normalizedBasePath.length) || "/");
      }
    }

    for (const candidate of candidatePaths) {
      const route = mockRoutes.find((r) => r.test.test(candidate));
      if (route) {
        return route.handler(candidate, { ...init, headers });
      }
    }
  }

  const response = await fetch(url, { ...init, headers });
  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const data = await response.json();
      // Special-case onboarding requirement to enable client handling
      if (response.status === 403 && data?.error === "onboarding_required") {
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("onboarding-required", { detail: data }),
          );
        }
      }
      // If server says we're unauthorized, clear auth and notify app
      if (response.status === 401) {
        clearAuth();
        clearBiometricCredential();
        resetAuthCache();
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("session-expired"));
        }
      }
      message = data?.message || data?.error || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  // Attempt to parse JSON response, return null if none
  try {
    return await response.json();
  } catch {
    return null;
  }
}
